// Каркас страницы: боковая панель, верхняя строка, содержимое раздела и общие окна.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { boot } from "./boot";
import { HelpDialog, helpStore, LoginDialog } from "./components/Dialogs";
import { DocPicker } from "./components/DocPicker";
import { Sidebar } from "./components/Sidebar";
import { Topbar } from "./components/Topbar";
import { UpdateBanner } from "./components/UpdateBanner";
import { DocViewer } from "./components/Viewer";

const MOBILE = "(max-width: 860px)";
const COLLAPSED_KEY = "pravosvod.sidebarCollapsed";

function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }   // меню открыто по умолчанию
}

export function App({ children }: { children: ReactNode }) {
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE).matches);
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const openRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const query = window.matchMedia(MOBILE);
    const change = () => { setMobile(query.matches); setNavOpen(false); };
    query.addEventListener("change", change);
    return () => query.removeEventListener("change", change);
  }, []);

  useEffect(() => { document.body.classList.toggle("nav-open", navOpen); }, [navOpen]);
  useEffect(() => {
    document.body.classList.toggle("sidebar-collapsed", collapsed);
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0"); } catch { /* необязательная настройка */ }
  }, [collapsed]);

  // Другие части страницы закрывают меню, снимая класс nav-open (например, переход к окну входа).
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (!document.body.classList.contains("nav-open")) setNavOpen(false);
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const closeNav = () => {
    setNavOpen(false);
    if (!mobile) setCollapsed(true);
    openRef.current?.focus();
  };
  const openNav = () => {
    if (mobile) setNavOpen(true); else setCollapsed(false);
    closeRef.current?.focus();
  };

  // Открытое меню на телефоне: Escape закрывает, Tab не уходит за пределы меню.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeNav();
      if (event.key === "Tab") {
        const focusable = [...document.querySelectorAll<HTMLElement>("#sidebar a[href], #sidebar button:not(:disabled), #sidebar input")]
          .filter((node) => node.getClientRects().length && node.tabIndex >= 0);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  return (
    <>
      <div className="layout">
        <Sidebar onClose={closeNav} closeRef={closeRef} />
        <div className="scrim" onClick={closeNav} />
        <div className="main">
          <Topbar openRef={openRef} expanded={mobile ? navOpen : !collapsed} onOpen={openNav} onHelp={() => helpStore.set(helpStore.get() + 1)} />
          <UpdateBanner />
          <main className="content">{children}</main>
          <DocViewer />
        </div>
      </div>
      <HelpDialog />
      <DocPicker />
      {boot.isGuest && <LoginDialog />}
    </>
  );
}
